import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { CuratorBubble } from "../components/ui/editorial/CuratorBubble.js";
import { getTelegram } from "../lib/telegram.js";
import { trpc } from "../trpc.js";

interface FieldProps {
  label: string;
  children: JSX.Element;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block border-t border-ink/12 pt-[14px]">
      <span className="editorial-meta text-ink/55">{label}</span>
      <div className="mt-[6px]">{children}</div>
    </label>
  );
}

export function Basics(): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [error, setError] = useState<string | null>(null);

  // В бете — только Москва; выбор города заблокирован.
  const city = "Москва";

  const utils = trpc.useUtils();
  const update = trpc.profile.update.useMutation({
    onSuccess: async () => {
      await utils.me.get.invalidate().catch(() => {
        /* noop — invalidation errors are non-critical */
      });
      navigate("/photo");
    },
    onError: (e) => {
      // В MVP-демо API может быть недоступен (tunnel off / VITE_API_URL не задан).
      // Не блокируем юзера сеткой — идём дальше, реальные валидации пусть ловят
      // при живом бэке.
      const msg = e.message;
      if (/Failed to fetch|NetworkError|ECONNREFUSED/i.test(msg)) {
        navigate("/photo");
        return;
      }
      setError(msg || "не удалось сохранить");
    },
  });

  const canProceed =
    name.trim().length >= 2 && Number(age) >= 18 && Number(age) <= 90 && !update.isPending;

  const onNext = () => {
    setError(null);
    const payload = {
      firstName: name.trim(),
      age: Number(age),
      city,
      confirmAdult: true as const,
    };
    // Вне Telegram (preview / dev-frame) — просто переход, без API.
    if (!getTelegram()) {
      navigate("/photo");
      return;
    }
    update.mutate(payload);
  };

  const inputCx =
    "w-full bg-transparent font-display font-light text-[26px] leading-[1.1] tracking-[-0.015em] text-ink outline-none placeholder:text-ink/30";

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="знакомство" />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[34px]">
        <div className="editorial-kicker text-accent">Спутник · представьтесь</div>

        <div className="mt-[18px] pr-[18px]">
          <CuratorBubble signature="Спутник">
            «Прежде чем я буду подбирать людей — расскажите немного о себе.»
          </CuratorBubble>
        </div>

        <div className="mt-[34px] space-y-[18px]">
          <Field label="Как к вам обращаться">
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="ваше имя"
              className={inputCx}
              autoComplete="given-name"
            />
          </Field>

          <Field label="Сколько вам лет">
            <input
              value={age}
              onChange={(e) => {
                setAge(e.target.value.replace(/\D/g, "").slice(0, 2));
              }}
              placeholder="возраст"
              inputMode="numeric"
              className={inputCx}
            />
          </Field>

          <div className="block border-t border-ink/12 pt-[14px]">
            <div className="editorial-meta text-ink/55">Город</div>
            <div className="mt-[6px] flex items-baseline justify-between gap-[12px]">
              <span className="font-display font-light text-[26px] leading-[1.1] tracking-[-0.015em] text-ink">
                {city}
              </span>
              <span className="editorial-meta text-accent">только в бете</span>
            </div>
            <p className="mt-[10px] font-display italic font-light text-[13px] leading-[1.45] text-ink/55">
              Пока мы работаем только по Москве — так Спутник успевает собрать живых людей рядом.
              Остальные города откроем после беты.
            </p>
          </div>
        </div>
      </div>

      <div className="px-[22px] pb-[24px] pt-[16px]">
        {error && <div className="mb-[12px] editorial-meta text-velvet">{error}</div>}
        <PrimaryButton disabled={!canProceed} onClick={onNext}>
          {update.isPending ? "…" : "Дальше"}
        </PrimaryButton>
      </div>
    </Screen>
  );
}
